import React, { createContext, useContext, useEffect, useState } from "react";
import { trackEvent } from "../analytics";
import { AUTH_REDIRECT_STORAGE_KEY } from "../lib/analyticsEvents";
import { logProductEvent, PRODUCT_EVENTS } from "../lib/productEvents";
import { clearWeekAgenda } from "../lib/chatSessionCache";
import { clearHomeSnapshot } from "../lib/homeSnapshot";
import { clearPendingMutations } from "../lib/pendingMutations";
import { supabase } from "../lib/supabase";

const AuthContext = createContext(null);

/** One app_opened row per document load, including React Strict Mode remounts. */
let appOpenLogged = false;

function logAppOpenedOnce() {
  if (appOpenLogged) return;
  appOpenLogged = true;
  logProductEvent(PRODUCT_EVENTS.app_opened);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) logAppOpenedOnce();
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (event === "SIGNED_IN" && session?.user) {
        logAppOpenedOnce();
        const provider =
          session.user.app_metadata?.provider ||
          session.user.identities?.[0]?.provider ||
          "email";
        let redirectPath;
        try {
          redirectPath = sessionStorage.getItem(AUTH_REDIRECT_STORAGE_KEY);
          sessionStorage.removeItem(AUTH_REDIRECT_STORAGE_KEY);
        } catch {
          redirectPath = undefined;
        }
        trackEvent("login", {
          method: provider === "google" ? "google" : "email",
          ...(redirectPath ? { redirect_path: redirectPath } : {}),
        });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithPassword = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error) throw error;
  };

  const signUp = async (email, password) => {
    const { error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
    });
    if (error) throw error;
  };

  const signInWithGoogle = async (redirectPath = "/") => {
    const redirectTo = `${window.location.origin}${redirectPath.startsWith("/") ? redirectPath : `/${redirectPath}`}`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    clearWeekAgenda();
    clearHomeSnapshot();
    clearPendingMutations();
    await supabase.auth.signOut();
  };

  const value = {
    user,
    loading,
    signInWithPassword,
    signUp,
    signInWithGoogle,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
